"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BizeSorunForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-lg border border-border bg-card p-8 text-center shadow-xl md:p-12">
        <div className="flex size-16 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <h3 className="font-default text-2xl font-bold text-primary">
          ¡Solicitud Enviada!
        </h3>
        <p className="font-default text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5 font-default">
          Enviar otra solicitud
        </Button>
      </div>;
  }
  return <div className="rounded-lg border border-border bg-card p-8 shadow-md md:p-12">
      <div className="mb-8 text-center">
        <h2 className="font-default mb-2 text-3xl font-bold text-primary md:text-4xl">
          Solicitar Demo
        </h2>
        <p className="font-default text-foreground">
          Descubra cómo nuestras soluciones de telecomunicaciones pueden potenciar su empresa.
        </p>
      </div>

      <Form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="name" rules={{
          required: "El nombre es obligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground font-semibold">Nombre Completo</FormLabel>
                <FormControl>
                  <Input placeholder="Ej: Juan Pérez" className="border-input bg-transparent focus-visible:ring-primary rounded-md" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company" rules={{
          required: "La empresa es obligatoria"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground font-semibold">
                  Empresa
                </FormLabel>
                <FormControl>
                  <Input placeholder="Nombre de su organización" className="border-input bg-transparent focus-visible:ring-primary rounded-md" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="email" rules={{
          required: "El correo es obligatorio",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Ingrese un correo válido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground font-semibold">
                  Correo Corporativo
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="juan@empresa.com" className="border-input bg-transparent focus-visible:ring-primary rounded-md" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="solution" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default text-foreground font-semibold">
                  Solución de Interés
                </FormLabel>
                <FormControl>
                  <select className="flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base text-foreground shadow-sm transition-all outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      Seleccione una solución
                    </option>
                    <option value="MS Teams" className="bg-background">Integración MS Teams</option>
                    <option value="AI Agents" className="bg-background">Agentes de IA</option>
                    <option value="PBX-Vtec" className="bg-background">PBX-Vtec Omnicanal</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "El mensaje es obligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground font-semibold">Requerimientos Específicos</FormLabel>
              <FormControl>
                <Textarea placeholder="Cuéntenos sobre sus necesidades de comunicación..." className="min-h-[120px] border-input bg-transparent focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="rounded-md border border-destructive bg-destructive/10 p-4">
            <p className="font-default text-sm text-destructive">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary py-6 text-lg font-bold text-secondary-foreground shadow-sm transition-all hover:scale-[1.02] hover:shadow-md animate-pulse">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Procesando...
            </> : "Solicitar Demo Ahora"}
        </Button>
      </Form>
    </div>;
}
export default function SolicitarDemo() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224735" id="solicitar-demo" className="relative overflow-hidden bg-background py-14 text-foreground md:py-16 shadow-inner">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/telecom_office_background.png)] bg-cover bg-center opacity-5 grayscale-[50%]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
      </div>

      {}
      <div className="absolute top-1/4 left-0 z-0 h-96 w-96 -translate-x-1/2 rounded-lg bg-primary/5 blur-[120px]"></div>
      <div className="absolute right-0 bottom-0 z-0 h-96 w-96 translate-x-1/3 translate-y-1/3 rounded-lg bg-secondary/5 blur-[120px]"></div>

      <div ref={ref} className={`relative z-10 mx-auto w-full max-w-3xl px-4 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <FormProvider formKey="demo_form_omnichannel" formId="demo_form_omnichannel" sectionName="solicitar-demo" defaultValues={{
        name: "",
        email: "",
        company: "",
        solution: "",
        message: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        company: {
          required: true
        },
        message: {
          required: true
        }
      }} submitText="Solicitar Demo Ahora" successMessage="Su solicitud ha sido recibida. Un especialista de Avtec se pondrá en contacto con usted en breve." errorMessage="Hubo un error al enviar su solicitud. Por favor, verifique los datos e intente nuevamente.">
          <BizeSorunForm />
        </FormProvider>
      </div>
    </section>;
}